import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { errorMessage } from "@/api/client";
import type { Item, RunDetail } from "@/api/runs";
import Icon from "@/components/icon";
import { List } from "@/components/list";
import Loader from "@/components/loader";
import StatusTag from "@/components/status-tag";
import Text from "@/components/text";
import { useRunItems } from "@/components/useRunItems";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDuration, formatTime, money, shortUuid } from "@/lib/format";
import { reasonLabel } from "@/lib/reasons";

const STATES = [
  ["all", "All"],
  ["ok", "Registered"],
  ["failed", "Failed"],
  ["running", "In progress"],
  ["pending", "Not started"],
] as const;

const STEPS: Record<string, string> = {
  baseline: "Reading code page",
  send_code: "Requesting code",
  resend_code: "Resending code",
  otp_wait: "Waiting for code",
  email: "Setting up email",
  sign_in: "Signing in",
  check_password: "Checking password",
};

const ANY = "any";
// Typing in the search box should not fire a query per keystroke.
const SEARCH_DEBOUNCE_MS = 300;
// Load the next page once this much of what is loaded has been scrolled past, so the
// rows are there before the list runs out.
const PREFETCH_AT = 0.75;

/**
 * The run's numbers, a page at a time. The filter lives in the URL, so it survives
 * polls and reloads, and it is applied by the server: a run of 100k numbers never
 * arrives in the browser at once.
 */
export function ItemsTable({ run, pollMs }: { run: RunDetail; pollMs: number | false }) {
  const [params, setParams] = useSearchParams();
  const state = params.get("state") ?? "all";
  const reason = params.get("reason") ?? ANY;
  const rawQuery = params.get("q") ?? "";
  const q = useDebounced(rawQuery.trim(), SEARCH_DEBOUNCE_MS);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all" && value !== ANY) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const { items, total, hasMore, loadMore, isPending, isLoadingMore, error } = useRunItems(run.id, pollMs, {
    state,
    reason: reason === ANY ? undefined : reason,
    q: q || undefined,
  });

  const counts: Record<string, number> = {
    all: run.counts.total,
    ok: run.counts.ok,
    failed: run.counts.failed,
    running: run.counts.running,
    pending: run.counts.pending,
  };
  // Counts and reasons come from the run's own tallies, so they cover every number in
  // the run, not only the rows loaded so far.
  const reasons = run.reasons.map((r) => [r.reason, r.count] as const);

  // Past three quarters of what is loaded, fetch the next page: the rows are there
  // before the list runs out, so scrolling never stops on a spinner.
  const box = useRef<HTMLDivElement | null>(null);
  const prefetch = useCallback(() => {
    const el = box.current;
    if (!el || !hasMore) return;
    if ((el.scrollTop + el.clientHeight) / el.scrollHeight >= PREFETCH_AT) loadMore();
  }, [hasMore, loadMore]);
  // A page that lands while the reader is standing still past the mark takes the next
  // one too, rather than waiting for a scroll that may not come.
  useEffect(prefetch, [prefetch, items.length]);

  const loading = isPending;
  return (
    <List>
      <div className="flex flex-wrap items-center gap-3 px-(--list-inset) pt-4 pb-3">
        <Tabs value={state} onValueChange={(v) => setParam("state", v)}>
          <TabsList className="w-fit">
            {STATES.map(([value, label]) => (
              <TabsTrigger key={value} value={value} className="gap-1.5 px-3 normal-case">
                {label}
                <span className="text-xs text-muted-foreground tabular-nums">{counts[value] ?? 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Select value={reason} onValueChange={(v) => setParam("reason", v)}>
          <SelectTrigger size="sm" className="min-w-44" aria-label="Failure reason">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            <SelectItem value={ANY}>Any reason</SelectItem>
            {reasons.map(([r, n]) => (
              <SelectItem key={r} value={r}>
                {reasonLabel(r)} ({n})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative ms-auto w-full sm:w-64">
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="Search numbers"
            placeholder="Number or account"
            className="h-(--control-height-sm) ps-9 text-sm"
            defaultValue={params.get("q") ?? ""}
            onChange={(e) => setParam("q", e.target.value)}
          />
        </div>
      </div>

      {error ? <Text.Danger className="px-(--list-inset) pb-3">{errorMessage(error)}</Text.Danger> : null}

      <div ref={box} onScroll={prefetch} data-items-scroll className="max-h-[70vh] overflow-auto border-t">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              <TableHead className="w-12 text-end">#</TableHead>
              <TableHead>Number</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Detail</TableHead>
              <TableHead>Account</TableHead>
              <TableHead className="text-end">Price</TableHead>
              <TableHead>Review</TableHead>
              <TableHead className="text-end">Tries</TableHead>
              <TableHead className="text-end">Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <Row key={item.id} item={item} />
            ))}
          </TableBody>
        </Table>
        {loading && (
          <div className="grid place-items-center py-10 text-muted-foreground">
            <Loader />
          </div>
        )}
        {!loading && items.length === 0 && (
          <Text.Hint as="p" className="px-(--list-inset) py-10 text-center">
            {run.counts.total ? "No numbers match this filter." : "No numbers."}
          </Text.Hint>
        )}
        {hasMore && (
          <div className="grid place-items-center py-4">
            {isLoadingMore ? (
              <Loader />
            ) : (
              <Button variant="outline" size="sm" onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>
      <Text.Hint as="p" size="caption" className="border-t px-(--list-inset) py-2.5 tabular-nums">
        Showing {items.length} of {total}
      </Text.Hint>
    </List>
  );
}

/** Waits for typing to stop before the value is used, so a search is one query. */
function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return settled;
}

const Row = memo(function Row({ item }: { item: Item }) {
  return (
    <TableRow>
      <TableCell className="text-end text-muted-foreground">{item.line_no}</TableCell>
      <TableCell>
        <span className="font-medium">{item.phone}</span>
        {item.country && <Text.Hint size="caption"> {item.country}</Text.Hint>}
      </TableCell>
      <TableCell>
        <StatusTag state={item.state} />
      </TableCell>
      <TableCell className="max-w-72">
        <Detail item={item} />
      </TableCell>
      <TableCell title={item.account_uuid ?? undefined}>
        {item.account_uuid ? <Text.Accent>{shortUuid(item.account_uuid)}</Text.Accent> : null}
      </TableCell>
      <TableCell className="text-end">{item.state === "failed" ? "" : money(item.price)}</TableCell>
      <TableCell>
        <Review item={item} />
      </TableCell>
      <TableCell className="text-end">{item.attempts || ""}</TableCell>
      <TableCell className="text-end text-muted-foreground">{formatDuration(item.duration_ms)}</TableCell>
    </TableRow>
  );
});

function Detail({ item }: { item: Item }) {
  if (item.state === "running") {
    return (
      <Text.Hint>
        {STEPS[item.step ?? ""] ?? "Starting"}
        {item.attempts > 1 ? `, try ${item.attempts}` : ""}
      </Text.Hint>
    );
  }
  if (item.state === "failed" || (item.state === "pending" && item.reason)) {
    return (
      <div className="flex min-w-0 flex-col" title={item.detail ?? undefined}>
        <Text.Danger className="truncate">{reasonLabel(item.reason)}</Text.Danger>
        <Text.Hint size="caption" className="truncate">
          {item.reason}
        </Text.Hint>
      </div>
    );
  }
  if (item.state === "ok" && item.detail) return <Text.Hint>{item.detail}</Text.Hint>;
  return null;
}

function Review({ item }: { item: Item }) {
  switch (item.verify_status) {
    case "accepted":
      return (
        <div className="flex items-center gap-2">
          <StatusTag state="accepted" />
          {item.email_cost ? <Text.Hint size="caption">email {money(item.email_cost)}</Text.Hint> : null}
        </div>
      );
    case "rejected":
      return (
        <div className="flex items-center gap-2">
          <StatusTag state="rejected" />
          <Text.Hint size="caption">{item.rejection_reason_detail ?? item.rejection_reason ?? ""}</Text.Hint>
        </div>
      );
    case "pending":
      return <Text.Hint>{item.verify_next_at ? `Next check ${formatTime(item.verify_next_at)}` : "Waiting"}</Text.Hint>;
    case "unknown":
      return <StatusTag state="unknown" />;
    default:
      return null;
  }
}
